import React from "react";
import { motion, useReducedMotion } from "framer-motion";

const STARS = Array.from({ length: 46 }, (_, i) => ({
  id: i,
  top: Math.random() * 100,
  left: Math.random() * 100,
  size: Math.random() * 2 + 1,
  delay: Math.random() * 4,
  dur: 2.6 + Math.random() * 3,
}));

const ZODIAC = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];

// Cosmic layer: drifting stars, orbiting planets, zodiac ring, sacred geometry.
export default function CosmicBackground({ dense = false }) {
  const reduce = useReducedMotion();

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10" data-testid="cosmic-background">
      <div className="absolute inset-0 bg-gradient-to-b from-[#F5FAF9] via-[#FBFAF7] to-[#F7F4EE]" />
      <div className="pattern-sacred absolute inset-0 opacity-30" />
      <span className="orb orb-gold h-[26rem] w-[26rem] -top-32 -left-24" />
      <span className="orb orb-red h-80 w-80 top-1/3 -right-20" />

      {STARS.slice(0, dense ? STARS.length : 26).map((s) => (
        <motion.span
          key={s.id}
          className="absolute rounded-full bg-tara-teal"
          style={{ top: `${s.top}%`, left: `${s.left}%`, width: s.size, height: s.size }}
          animate={reduce ? undefined : { opacity: [0.15, 0.9, 0.15], scale: [1, 1.5, 1] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* Zodiac ring */}
      <div className="absolute -right-40 top-10 hidden lg:block">
        <motion.div
          className="relative h-[34rem] w-[34rem] rounded-full border border-tara-teal/15"
          animate={reduce ? undefined : { rotate: 360 }}
          transition={{ duration: 190, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute inset-12 rounded-full border border-dashed border-tara-vermilion/15" />
          <div className="absolute inset-28 rounded-full border border-tara-teal/10" />
          {ZODIAC.map((z, i) => {
            const angle = (i / 12) * Math.PI * 2;
            return (
              <span
                key={z}
                className="absolute font-serif text-lg text-tara-teal/45"
                style={{
                  top: `${50 + 47 * Math.sin(angle)}%`,
                  left: `${50 + 47 * Math.cos(angle)}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                {z}
              </span>
            );
          })}
        </motion.div>
      </div>

      {/* Floating planets */}
      {[
        { size: 54, top: "18%", left: "12%", dur: 13, from: "#B4232A", to: "#8E1B22" },
        { size: 30, top: "62%", left: "8%", dur: 17, from: "#0E5A5E", to: "#4FA8A5" },
        { size: 22, top: "34%", left: "46%", dur: 21, from: "#4FA8A5", to: "#0E5A5E" },
      ].map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full opacity-30 blur-[1px]"
          style={{
            top: p.top, left: p.left, width: p.size, height: p.size,
            background: `radial-gradient(circle at 32% 30%, ${p.from}, ${p.to})`,
          }}
          animate={reduce ? undefined : { y: [0, -26, 0], x: [0, 12, 0] }}
          transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}
